import React, { useState } from "react";
import {
  Button,
  Card,
  CardBody,
  Col,
  UncontrolledTooltip,
} from "reactstrap";
import { useNavigate } from "react-router";
//Import Icons
import DeleteModal from "src/common/DeleteModal";
import { deleteUser } from "src/slices/thunks";
import { useDispatch } from "react-redux";
import { getUsers as onGetUsers } from "src/slices/thunks";
import { setAgency } from "src/slices/Auth/Agency/thunk";
import { loginById } from "src/slices/thunks";
import { toast } from 'react-toastify';
import {
  setSelectedAgency,
  setSelectedOrder,
  setSelectedLineItems,
  setStartDate,
  setEndDate,
} from "../../../slices/Filters/reducer";
import { sendInvite } from "src/components/Common/sentInvite";
import useIsMobile from "src/hooks/useIsMobile";

interface CardContactProps {
  user: any;
  agency: any;
  onUserDeleted: () => void;
}



const CardContact: React.FC<CardContactProps> = ({ user, agency, onUserDeleted }) => {

  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [deleteModal, setDeleteModal] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const handleDeleteUser = (users_id: number) => {
    if (users_id) {
      dispatch(deleteUser(users_id));
      setDeleteModal(false);
      setTimeout(() => {
        onUserDeleted();
        toast.success('Team user deleted successfully!');
      }, 1000);
    }
  };

  const handleUserLogin = async () => {
    setLoginLoading(true);

    try {
      const userId = user.users_id;
      const currentAuthUser = localStorage.getItem("authUser");

      if (currentAuthUser) {
        localStorage.setItem("tempAuthUser", currentAuthUser);
      }

      // Clear filters
      if (user.user_type != 'Client')
        dispatch(setSelectedAgency(''));
      dispatch(setSelectedOrder(''));
      dispatch(setSelectedLineItems(''));
      dispatch(setStartDate(''));
      dispatch(setEndDate(''));

      // Wait for login to complete first
      await dispatch(loginById(userId));

      // Clear localStorage filters
      ["selectedOrder", "selectedStatus", "startDate", "endDate", "selectedLineItems", "selectedAgency"].forEach((key) =>
        localStorage.removeItem(key)
      );

      // Then wait for agency setup to complete before navigating
      await dispatch(setAgency({ agency_id: agency.data.Id }, navigate, true));

      // Add 500ms delay to ensure Redux state propagates
      await new Promise(resolve => setTimeout(resolve, 500));

    } catch (error) {
      console.error("Login failed:", error);
      toast.error('Failed to login as user. Please try again.');
      setLoginLoading(false);
    }
  };

  const isInvite =
  user.status === 'Inactive' && (user.var_key === '' || user.var_key === null);

  const isTouch = typeof window !== "undefined" && window.matchMedia("(pointer: coarse)").matches;

  const getInitials = (name: string) => {
    if (!name) return '';
    const words = name.trim().split(' ').filter(Boolean);
    const firstInitial = words[0]?.charAt(0).toUpperCase() || '';
    const secondInitial = words.length > 1 ? words[1]?.charAt(0).toUpperCase() : '';
    return firstInitial + secondInitial;
  };
  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={() => handleDeleteUser(user.users_id)}
        onCloseClick={() => setDeleteModal(false)}
      />
      <Col xl="3" lg="4" md="6" sm="12" className="d-flex">
        <Card
          style={{
            borderRadius: "6px",
            boxShadow: "0 1px 5px 0 rgba(41, 3, 3, 0.05)",
          }}
          className="flex-fill d-flex flex-column"
        >
          <CardBody className="p-3">
            <div className="d-flex flex-column">
              {/* Top row: Profile + Name */}
              <div className="d-flex align-items-center mb-2 custom-border-bottom" style={{ paddingBottom: "10px" }} >
                <div
                  className="avatar rounded-circle"
                  style={{
                    width: 28,
                    height: 28,
                    marginRight: "8px",
                    overflow: "hidden",
                    flexShrink: 0,
                  }}
                >
                  {user.profile_picture_url ? (
                    <img
                      src={user.profile_picture_url}
                      alt=""
                      className="img-fluid rounded-circle"
                      style={{ width: "100%", height: "100%", objectFit: "cover", border: "1.2px solid #bac7df", display: "block" }}
                    />
                  ) : (
                    <div
                      style={{
                        width: "100%",
                        height: "100%",
                        borderRadius: "50%",
                        backgroundColor: "#607d8b",
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: "bold",
                        fontSize: 10,
                      }}
                    >
                      {getInitials(user.name)}
                    </div>
                  )}
                </div>

                {/* User Name */}
                <h5 className="font-size-15 mb-0" style={{ width: "calc(100% - 48px)" }}>
                  <a
                    className="text-dark user-name text-capitalize"
                    style={{ cursor: "pointer" }}
                    onClick={() =>
                      navigate("/add-edit-user", {
                        state: { user: user, from: "/team" },
                      })
                    }
                  >
                    {user.name}
                  </a>
                </h5>
              </div>
              {/* Email */}
              <p
                className="text-muted  my-1 d-flex align-items-center"
                style={{
                  maxWidth: "100%",
                  padding: "2px",
                  whiteSpace: "normal",
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                }}
                title={user.email}
              >
                <span className="text-primary" style={{ marginRight: '8px' }}>
                  <i className="uil uil-envelope-alt me-1 accent-color" ></i>
                </span>
                {user.email}
              </p>
              {/* Mobile */}
              <p className="text-muted text-truncate my-1 " style={{ padding: "2px" }}>
                <span className="text-primary">
                  <i className="uil uil-phone-alt accent-color" style={{ marginRight: '8px' }}></i>{" "}
                </span>
                {user.mobile || 'N/A'}
              </p>
            </div>
          </CardBody>
          <div className="btn-group btn-icon" role="group">
            <button
              type="button"
              className="btn btn-outline-light"
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              id={`Profile_${user.users_id}`}
              onClick={() => {
                navigate("/add-edit-user", { state: { user: user, from: '/team' } });
              }}
            >
              <i className="uil uil-user" style={{ color: "#038edc" }}></i>
              <UncontrolledTooltip className="tooltip" placement="top" target={`Profile_${user.users_id}`}
                autohide={true}
                trigger={isMobile ? "" : "hover focus"}>
                Profile
              </UncontrolledTooltip>
            </button>
            {user.status === 'Inactive' ? (
              <button
                type="button"
                className="btn btn-outline-light"
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                id={`send_invite_${user.users_id}`}
                onClick={() => sendInvite(user, agency)}
              >
                {user.status === 'Inactive' && (user.var_key === '' || user.var_key === null) ? (
                  <i className="uil uil-envelope-alt" style={{ color: "aqua" }}></i>
                ) : (
                  <i className="uil uil-envelope-redo text-danger"></i>
                )}
                <UncontrolledTooltip placement="top" target={`send_invite_${user.users_id}`}
                  autohide={true}
                  trigger={isMobile ? "" : "hover focus"}>
                  {isInvite ? "Send Invite" : "Send Re-Invite"}
                </UncontrolledTooltip>
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-outline-light"
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                id={`single_sign_on_${user.users_id}`}
                onClick={handleUserLogin}
              >
                <i className="uil uil-sign-out-alt text-success"></i>
                <UncontrolledTooltip placement="top" target={`single_sign_on_${user.users_id}`}
                  autohide={true}
                  trigger={isMobile ? "" : "hover focus"}
                >
                  Sign In
                </UncontrolledTooltip>
              </button>
            )}
            <button
              type="button"
              className="btn btn-outline-light"
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              id={`Delete_${user.users_id}`}
              onClick={() => {
                // Hide tooltip before opening modal
                const tooltipElement = document.getElementById(`Delete_${user.users_id}`);
                if (tooltipElement) {
                  tooltipElement.blur();
                }
                setDeleteModal(true);
              }}
            >
              <i className="mdi mdi-trash-can text-danger me-1"></i>
              <UncontrolledTooltip placement="top" target={`Delete_${user.users_id}`}
                autohide={true}
                trigger={isMobile ? "" : "hover focus"}>
                Delete
              </UncontrolledTooltip>
            </button>
          </div>
        </Card>
      </Col>

      {/* Loading Overlay */}
      {loginLoading && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <div className="spinner-border text-light" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-light mt-3" style={{ fontSize: '16px', fontWeight: '500' }}>
            Logging in , please wait...
          </p>
        </div>
      )}
    </React.Fragment>
  );
};

export default CardContact;
